import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "../../../stores";
import {KoiFacade} from "../../../components/koi-facade/KoiFacade";
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    TableCell,
    TablePagination,
    TableRow,
    Typography
} from "@mui/material";
import {generatePath, useNavigate, useParams} from "react-router";
import {routes} from "../../../routing/routes";
import {ApprovalStore} from "../../../stores/ApprovalStore";
import moment from "moment";
import {AccountCircleOutlined, CalendarTodayOutlined, DatasetOutlined, Refresh} from "@mui/icons-material";
import type {MithraTableHeader} from "../../../components/data-table/MithraTable";
import {MithraTable} from "../../../components/data-table/MithraTable";
import {CurrencyComponent} from "../../../components/currency-component/CurrencyComponent";
import {CategorizationViewOnlyComponent} from "../../../components/categorization/CategorizationViewOnlyComponent";
import {fromFieldsCategoriesOrUncat} from "../../../services/ApiHelpers";
import {ApprovalStatusEnum} from "../../../services/classes/AiClasses";
import {EllipsisCutoff} from "../../../components/ellipsis-cutoff/EllipsisCutoff";
import {environment} from "../../../env";
import {useRollbar} from "@rollbar/react";

const HEADERS: MithraTableHeader[] = [
    {headerText: 'Supplier'},
    {headerText: 'Name'},
    {headerText: 'Amount'},
    {headerText: 'Description'},
    {headerText: 'Previous category', className: 'width-20'},
    {headerText: 'Requested category', className: 'width-20'},
]

export const ApprovalCategorizationPage: React.FC = observer(() => {
    const {approvalStore, bagStore, p} = useStores();
    const rollbar = environment.rollbarConfig ? useRollbar() : console;
    const pStore = p;
    const navigate = useNavigate();
    const routeParams = useParams();
    const taxonomySize = bagStore.taxonomy_size;
    const [dialogFor, setDialogFor] = React.useState<'' | 'Accept' | 'Reject'>('');
    const approvalId = Number(routeParams['approvalId']);

    useEffect(() => {
        if (!approvalId) {
            rollbar.warn(`Approval ID (${approvalId}) is not provided on ApprovalCategorizationPage`);
        }
        approvalStore.approvalItem.cleanup();
        approvalStore.categorizationPartList.cleanup();
        approvalStore.approvalItem.request({id: approvalId});
        // Reset page params to 1: When user is visiting approval request A and goes to list then to B, page number of
        //  histories for B should be 1 again. Note that we can't use setCategorizationPartListFilters since store's
        //  approvalId is not set yet and is waiting to be retrieved from API
        approvalStore.categorizationPartListFilters.setFilters([['page', '1']]);
        approvalStore.categorizationPartList.request({
            id: approvalId,
            filters: approvalStore.categorizationPartListFilters.filters
        });
    }, [routeParams, approvalStore.approvalItem, approvalStore.categorizationPartList, approvalStore.categorizationPartListFilters, approvalId]);

    const createdByName = (approvalStore.approvalItem.result?.created_by.first_name ?? '')
        + ' ' + (approvalStore.approvalItem.result?.created_by.last_name ?? '');
    return (
        <KoiFacade
            title="Request details"
            back={routes.approval}
            backClass={""}
            className="approval-page categorization-approval-page"
        >
            <Grid container className="approval-info-header">
                <Grid size={2}>
                    <AccountCircleOutlined className="approval-avatar"/>
                </Grid>
                <Grid container direction="column" size={4}>
                    <Grid>
                        Requester
                        <h3>{createdByName}</h3>
                    </Grid>
                    <Grid>
                        Notes
                        <div className="approval-notes">{approvalStore.approvalItem.result?.notes}</div>
                    </Grid>
                </Grid>
                <Grid container direction="column" size={3}>
                    <Grid className="approval-info-type-date">
                        <DatasetOutlined/>
                        <div>
                            Dataset
                            <h4>
                                {approvalStore.approvalItem.result?.baseline_bag?.name}
                            </h4>
                        </div>
                    </Grid>
                    <Grid className="approval-info-type-date">
                        <Refresh/>
                        <div>
                            Type of change
                            <h4>
                                {approvalStore.approvalItem.result && ApprovalStore.showRequestType(approvalStore.approvalItem.result.request_type)}
                            </h4>
                        </div>
                    </Grid>
                    <Grid className="approval-info-type-date">
                        <CalendarTodayOutlined/>
                        <div>
                            Date & Time
                            <h4>{approvalStore.approvalItem.result && moment(approvalStore.approvalItem.result.created_timestamp).format('YYYY-MM-DD hh:mm A')}</h4>
                        </div>
                    </Grid>
                </Grid>
                <Grid size={3}>
                    <Grid container direction="column" alignItems="center" justifyContent="center">
                        <Grid>
                            <Typography variant="body2" color="textSecondary">Status</Typography>
                        </Grid>
                        <Grid>
                            <Chip size="medium"
                                  label={approvalStore.approvalItem.result && ApprovalStore.getApprovalStatusName(approvalStore.approvalItem.result.current_status.status)}
                                  className={'approval-accept-reject-btn ' + (approvalStore.approvalItem.result && approvalStore.approvalItem.result?.current_status.status_name.toLowerCase())}/>
                        </Grid>
                        {approvalStore.approvalItem.result?.current_status.status === ApprovalStatusEnum.PENDING && <>
                            <Grid>
                                <Button className="approval-accept-reject-btn accept"
                                        onClick={() => setDialogFor('Accept')}>
                                    Accept all
                                </Button>
                                <Button className="approval-accept-reject-btn reject"
                                        onClick={() => setDialogFor('Reject')}>
                                    Reject all
                                </Button>
                            </Grid>
                            <Grid>
                                <Alert severity="info">
                                    Processing a request may take a while. {/*minutes to hours*/}
                                    Multiple requests can be processed simultaneously, but reviews are blocked.
                                </Alert>
                            </Grid>
                        </>}
                    </Grid>
                </Grid>
            </Grid>
            <Grid container>
                <Grid size={12}>
                    <h3>Change list</h3>
                    <MithraTable
                        headers={HEADERS} loading={approvalStore.categorizationPartList.busy}
                        children={approvalStore.categorizationPartList.result?.results.map((p, i) => {
                            return <TableRow key={i}>
                                <TableCell>{p.part__supplier__s_name}</TableCell>
                                <TableCell>
                                    <EllipsisCutoff
                                        txt={p.part__p_name}
                                        max={pStore.p.p_name_ellipsis_cutoff ?? null}
                                    />
                                </TableCell>
                                <TableCell>
                                    <CurrencyComponent v={p.part__p_spend} nDigits={3}/>
                                </TableCell>
                                <TableCell>{p.part__p_description}</TableCell>
                                <TableCell>
                                    <CategorizationViewOnlyComponent
                                        category={fromFieldsCategoriesOrUncat(p, 'previous_cat', taxonomySize)}/>
                                </TableCell>
                                <TableCell>
                                    <CategorizationViewOnlyComponent
                                        category={fromFieldsCategoriesOrUncat(p, 'category_', taxonomySize)}/>
                                </TableCell>
                            </TableRow>
                        })}
                        footer={
                            <TableRow>
                                <TablePagination
                                    count={approvalStore.categorizationPartList.result?.count ?? 0}
                                    rowsPerPage={parseInt(approvalStore.categorizationPartListFilters.filters.find(i => i[0] === 'page_size')?.at(1) ?? '10')}
                                    page={parseInt(approvalStore.categorizationPartListFilters.filters.find(i => i[0] === 'page')?.at(1) ?? '1') - 1}
                                    onPageChange={(event, page: number) => {
                                        approvalStore.setCategorizationPartListFilters([['page', (page + 1).toString()]]);
                                    }}
                                    onRowsPerPageChange={(event) => {
                                        approvalStore.setCategorizationPartListFilters([['page_size', (event.target.value).toString()]]);
                                        approvalStore.setCategorizationPartListFilters([['page', '1']]);
                                    }}
                                />
                            </TableRow>}/>
                </Grid>
            </Grid>
            {/* Dialog */}
            <Dialog open={dialogFor !== ''} aria-labelledby="dialog-title" aria-describedby="dialog-description"
                    onClose={() => {
                        setDialogFor('');
                    }}>
                <DialogTitle id="dialog-title">
                    {dialogFor + ' request'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="dialog-description">
                        Are you sure you want to {dialogFor === 'Accept' ? 'accept' : 'reject'} the change
                        from&nbsp;{createdByName}?
                        {dialogFor === 'Accept' &&
                            <>
                                <br/>
                                It could take several minutes before all data is processed
                            </>}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setDialogFor('');
                    }}>cancel</Button>
                    <Button
                        variant="contained"
                        color={dialogFor === 'Accept' ? 'primary' : 'error'}
                        disabled={approvalStore.categorizationApply.busy}
                        onClick={() => {
                            approvalStore.categorizationApply.request({
                                approvalId: approvalId,
                                status: dialogFor === 'Accept' ? ApprovalStatusEnum.APPROVED : ApprovalStatusEnum.REJECTED
                            });
                            navigate(generatePath(routes.approval));
                        }}>
                        {dialogFor + ' request'}
                    </Button>
                </DialogActions>
            </Dialog>
        </KoiFacade>
    );
});
